import { profileTitle } from './seoSlug';

const BRAND = 'JamWith';
const SUFFIX = ` | ${BRAND}`;
const MAX_LENGTH = 60;

export const LOGIN_TITLE = 'Join JamWith - Music Collaboration Platform';
export const SIGNUP_TITLE = 'Sign Up To JamWith';
export const DISCOVER_TITLE = `Discover Music Collaborators${SUFFIX}`;

export function pageTitle(context: string): string {
    const text = context.replace(/\s+/g, ' ').trim();
    if (!text) return `Page${SUFFIX}`;
    const full = `${text}${SUFFIX}`;
    if (full.length <= MAX_LENGTH) return full;
    const budget = MAX_LENGTH - SUFFIX.length - 1;
    if (budget < 12) return full;
    return `${text.slice(0, budget).trimEnd()}…${SUFFIX}`;
}

export function sectionTitle(section: string, name?: string | null): string {
    const label = name?.replace(/\s+/g, ' ').trim();
    return pageTitle(label ? `${label} | ${section}` : section);
}

export function profileDocumentTitle(input: {
    displayName?: string | null;
    headline?: string | null;
    city?: string | null;
    skills?: Array<{ displayName?: string | null; name?: string | null }> | null;
}): string {
    return profileTitle(input);
}

export function jamBoardDocumentTitle(title?: string | null, fallback = 'Project'): string {
    const name = title?.replace(/\s+/g, ' ').trim() || fallback;
    return pageTitle(`${name} | JamBoard`);
}

export function eventDocumentTitle(title?: string | null): string {
    const name = title?.replace(/\s+/g, ' ').trim();
    return pageTitle(name ? `${name} | Events` : 'New Event | Events');
}

export function searchDocumentTitle(query?: string | null): string {
    const text = query?.replace(/\s+/g, ' ').trim();
    if (!text) return pageTitle('Search');
    return pageTitle(`Search: ${text}`);
}

export function onboardingStepTitle(step: string): string | null {
    switch (step) {
        case 'identity':
            return pageTitle('What Brings You Here');
        case 'skills':
            return pageTitle('Your Skills');
        case 'genres':
            return pageTitle('Your Genres');
        case 'influences':
            return pageTitle('Your Influences');
        case 'profile':
            return pageTitle('Your Profile Photo');
        default:
            return null;
    }
}

export function signupStepTitle(step: number): string {
    if (step === 0) return pageTitle('Referral Code');
    if (step === 2) return pageTitle('Sign Up Terms');
    if (step === 3) return pageTitle('Create Your Account');
    return SIGNUP_TITLE;
}

export type PaymentPageStatus = 'loading' | 'pending' | 'success' | 'error' | 'missing';

export function paymentSuccessTitle(status: PaymentPageStatus, isWalletTopUp: boolean): string {
    if (isWalletTopUp) {
        switch (status) {
            case 'pending':
                return pageTitle('Wallet Top-Up Processing');
            case 'success':
                return pageTitle('Wallet Topped Up');
            case 'error':
                return pageTitle('Wallet Top-Up Incomplete');
            case 'missing':
                return pageTitle('Wallet Checkout Not Found');
            default:
                return pageTitle('Confirming Wallet Top-Up');
        }
    }
    switch (status) {
        case 'pending':
            return pageTitle('Boost Processing');
        case 'success':
            return pageTitle('Boost Applied');
        case 'error':
            return pageTitle('Boost Activation Incomplete');
        case 'missing':
            return pageTitle('Boost Checkout Not Found');
        default:
            return pageTitle('Confirming Payment');
    }
}

const EXACT_TITLES: Record<string, string> = {
    '/': DISCOVER_TITLE,
    '/discover': DISCOVER_TITLE,
    '/login': LOGIN_TITLE,
    '/login/success': pageTitle('Signing In'),
    '/login/error': pageTitle('Login Problem'),
    '/signup': SIGNUP_TITLE,
    '/register': SIGNUP_TITLE,
    '/signup/success': pageTitle('Sign Up Complete'),
    '/signup/error': pageTitle('Sign Up Problem'),
    '/signup/terms': pageTitle('Sign Up Terms'),
    '/account/delete': pageTitle('Delete Account'),
    '/onboarding': SIGNUP_TITLE,
    '/onboarding/people': pageTitle('People to Jam With'),
    '/profile-completion': SIGNUP_TITLE,
    '/privacy-policy': pageTitle('Privacy Policy'),
    '/terms': pageTitle('Terms & Conditions'),
    '/liked': pageTitle('Liked Profiles'),
    '/jamboard': pageTitle('JamBoard - Music Projects'),
    '/collaborations': pageTitle('Active Collaborations'),
    '/profile': pageTitle('Profile'),
    '/settings': pageTitle('Settings'),
    '/people': pageTitle('People to Jam With'),
    '/notifications': pageTitle('Notifications'),
    '/bell': pageTitle('Notifications'),
    '/marketplace': pageTitle('Marketplace'),
    '/create-jam-session': pageTitle('Create a Jam'),
    '/admin': pageTitle('Admin Dashboard'),
    '/admin/departures': pageTitle('Departures'),
    '/admin/user-lookup': pageTitle('Admin User Lookup'),
    '/admin/user-mod': pageTitle('User Moderation'),
    '/jail': pageTitle('Account Disabled'),
    '/admin/logs': pageTitle('Admin Activity Logs'),
    '/admin/boosts': pageTitle('Boost Management'),
    '/admin/announce': pageTitle('Announcements'),
    '/admin/link-repair': pageTitle('Link Repair'),
    '/admin/referral-map': pageTitle('Referral Map'),
    '/admin/promos': pageTitle('Promos Management'),
    '/partner': pageTitle('Partner Dashboard'),
    '/partner/promos': pageTitle('Your Promos'),
    '/partner/billing': pageTitle('Partner Billing'),
    '/404': pageTitle('Page Not Found'),
    '/payment/success': pageTitle('Confirming Payment'),
    '/payment/cancel': pageTitle('Checkout Canceled'),
};

export function titleForLocation(pathname: string, search = ''): string {
    const path = normalizePath(pathname);
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

    if (path === '/search') return searchDocumentTitle(params.get('query'));
    if (path === '/admin/user-lookup') {
        const username = params.get('username');
        return username?.trim()
            ? sectionTitle('User Lookup', username)
            : EXACT_TITLES[path];
    }
    if (path === '/payment/success' && params.get('type') === 'wallet_topup') {
        return pageTitle('Confirming Wallet Top-Up');
    }
    if (path === '/payment/cancel') {
        return params.get('type') === 'wallet_topup'
            ? pageTitle('Wallet Top-Up Canceled')
            : EXACT_TITLES[path];
    }

    const exact = EXACT_TITLES[path];
    if (exact) return exact;

    const project = path.match(/^\/projects\/([^/]+)$/);
    if (project) return jamBoardDocumentTitle(null);

    const links = path.match(/^\/([^/]+)\/links$/);
    if (links) return sectionTitle('Links', decodeURIComponent(links[1]));

    if (path === '/directory' || path.startsWith('/directory/')) return pageTitle('Musicians');

    const profile = path.match(/^\/([^/]+)$/);
    if (profile) return pageTitle('Profile');

    return pageTitle('Page Not Found');
}

function normalizePath(pathname: string): string {
    const path = pathname.split('?')[0].split('#')[0] || '/';
    if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
    return path;
}

type TitleEntry = { id: number; title: string };

let nextTitleId = 1;
const titleStack: TitleEntry[] = [];
let currentPathname = '/';
let currentSearch = '';

export function applyDocumentTitle(): void {
    const top = titleStack[titleStack.length - 1];
    document.title = top ? top.title : titleForLocation(currentPathname, currentSearch);
}

export function syncRouteTitle(pathname: string, search: string): void {
    currentPathname = pathname;
    currentSearch = search;
    applyDocumentTitle();
}

export function pushDocumentTitle(title: string): () => void {
    const entry = { id: nextTitleId, title };
    nextTitleId += 1;
    titleStack.push(entry);
    applyDocumentTitle();
    return () => {
        const index = titleStack.findIndex((item) => item.id === entry.id);
        if (index >= 0) titleStack.splice(index, 1);
        applyDocumentTitle();
    };
}

export function resetDocumentTitleState(): void {
    titleStack.length = 0;
    nextTitleId = 1;
    currentPathname = '/';
    currentSearch = '';
}
